import { config } from "../config";
import { TSession, TProcedure } from "../utils/types";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefixAppointment = `${protocol}://${host}/clinics/v1/appointment`;

export type TCreateAppointmentRequest = {
  date: string; // ISO datetime string
  type: string;
  reason: string;
  unformatted_procedures?: string[];
  formatted_procedures?: TProcedure[];
  notes?: string;
  external_id?: string;
};

export type TCreateAppointmentResponse = {
  id: number;
  date: string;
  type: string;
  reason: string;
  unformatted_procedures?: string[];
  formatted_procedures?: TProcedure[];
  notes?: string;
  external_id?: string;
  created_at: string;
  updated_at: string;
};

export type TListAppointmentResponse = TCreateAppointmentResponse & {
  patient_uuid: string;
  patient_summary?: string | null;
};

export type TListAllAppointmentsResponse = {
  appointments: TListAppointmentResponse[];
};

export type TListAppointmentsForClinicResponse = {
  appointments: TListAppointmentResponse[];
};

export type TGetAppointmentsByPatientOptions = {
  includePatientSummary?: boolean;
};

export const createAppointment = async (
  session: TSession,
  patientId: string,
  data: TCreateAppointmentRequest,
): Promise<TCreateAppointmentResponse> => {
  const url = `${urlPrefixAppointment}/${session.clinicId}/patients/${patientId}/appointments`;

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create appointment: ${response.status}`);
  }

  return response.json();
};

export const updateAppointment = async (
  session: TSession,
  patientId: string,
  appointmentId: number,
  data: TCreateAppointmentRequest,
): Promise<TCreateAppointmentResponse> => {
  const url = `${urlPrefixAppointment}/${session.clinicId}/patients/${patientId}/appointments/${appointmentId}`;

  const response = await fetchWithAuthRetry(url, {
    method: "PUT",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to update appointment: ${response.status}`);
  }

  return response.json();
};

export const getAppointmentsByPatient = async (
  session: TSession,
  patientId: string,
  options?: TGetAppointmentsByPatientOptions,
): Promise<TListAppointmentResponse[]> => {
  const params = new URLSearchParams();

  if (options?.includePatientSummary) {
    params.append("include_patient_summary", "true");
  }

  const queryString = params.toString();
  const url = `${urlPrefixAppointment}/${session.clinicId}/patients/${patientId}/appointments${queryString ? `?${queryString}` : ""}`;

  const response = await fetchWithAuthRetry(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch appointments: ${response.status}`);
  }

  const data: TListAllAppointmentsResponse = await response.json();
  return data.appointments;
};

export const getAppointmentsForClinic = async (
  session: TSession,
  useDummyData: boolean = false,
): Promise<TListAppointmentResponse[]> => {
  const params = new URLSearchParams({
    use_dummy_data: String(useDummyData),
  });

  const url = `${urlPrefixAppointment}/${session.clinicId}/appointments?${params.toString()}`;

  const response = await fetchWithAuthRetry(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch clinic appointments: ${response.status}`);
  }

  const data: TListAppointmentsForClinicResponse = await response.json();
  return data.appointments;
};
